/// <reference types="@tableau/extensions-api-types" />

import {
  describeSavePermission,
  isPermissionDeniedError,
  resolveSavePermission,
  type SavePermissionReason,
  type SavePermissionState,
} from './save-permission';

/**
 * TableauSettingsManager: persists extension settings via the Tableau Settings API.
 *
 * All values are stored as JSON strings; the interfaces below describe the
 * parsed shapes.  The save-coalescing pattern (_saveInProgress / _pendingSave)
 * ensures only one saveAsync() call is in flight at a time.
 *
 * A save that cannot persist is reported through `onPermissionChange`. Callers
 * that report success to the user must consult `isReadOnly()` first: a denied
 * save resolves normally, so awaiting it proves nothing about persistence.
 */

export interface KronosConfig {
  worksheet?: string;
  labelField?: string;
  [key: string]: unknown;
}

export interface TreeData {
  content: string;
  fileName: string;
  timestamp: number;
}

export interface VisualizationSettings {
  colorScheme: string;
  labelType: string;
  showBranchLengths: boolean;
  branchLengthScale: string;
  nodeSize: number;
  layout: string;
  timestamp: number;
}

export interface UIState {
  [key: string]: unknown;
}

export interface AllSettings {
  config?: KronosConfig | null;
  treeData?: TreeData | null;
  visualization?: VisualizationSettings | null;
  phylocanvas?: Record<string, unknown> | null;
  uiState?: UIState | null;
  customColors?: Record<string, unknown> | null;
  leafCustomColors?: Record<string, unknown> | null;
}

interface TableauSettingsManagerModule {
  readonly KEYS: {
    readonly CONFIG: string;
    readonly TREE_DATA: string;
    readonly VISUALIZATION: string;
    readonly PHYLOCANVAS: string;
    readonly UI_STATE: string;
    readonly CUSTOM_COLORS: string;
    readonly LEAF_CUSTOM_COLORS: string;
    readonly RENDERER_TYPE: string;
  };
  _saveInProgress: boolean;
  _pendingSave: boolean;
  _readOnly: boolean;
  _permissionReason: SavePermissionReason | null;
  /**
   * Called once per reason when settings stop being persistable. Set by the
   * app layer to surface the condition in the UI.
   */
  onPermissionChange: ((state: SavePermissionState, message: { title: string; detail: string }) => void) | null;
  initialize(): Promise<void>;
  isReadOnly(): boolean;
  _reportPermission(reason: SavePermissionReason): void;
  _isPermissionDenied(error: unknown): boolean;
  _executeSave(): Promise<void>;
  saveConfig(config: KronosConfig): Promise<void>;
  loadConfig(): KronosConfig | null;
  saveTreeData(treeData: Pick<TreeData, 'content'> & Partial<Pick<TreeData, 'fileName'>>): Promise<void>;
  loadTreeData(): TreeData | null;
  saveVisualizationSettings(settings: Partial<VisualizationSettings>): Promise<void>;
  loadVisualizationSettings(): VisualizationSettings | null;
  savePhylocanvasSettings(settings: Record<string, unknown>): Promise<void>;
  loadPhylocanvasSettings(): Record<string, unknown> | null;
  saveRendererType(rendererType: string): Promise<void>;
  loadRendererType(): string | null;
  saveUIState(uiState: UIState): Promise<void>;
  loadUIState(): UIState | null;
  saveCustomColors(customColors: Record<string, unknown>): Promise<void>;
  loadCustomColors(): Record<string, unknown> | null;
  clearCustomColors(): Promise<void>;
  saveLeafCustomColors(leafCustomColors: Record<string, unknown>): Promise<void>;
  loadLeafCustomColors(): Record<string, unknown> | null;
  saveAllSettings(allSettings: AllSettings): Promise<void>;
  loadAllSettings(): AllSettings;
  clearSetting(key: string): Promise<void>;
  clearAllSettings(): Promise<void>;
  getAllRawSettings(): Record<string, string>;
}

export const TableauSettingsManager: TableauSettingsManagerModule = {
  KEYS: {
    CONFIG: 'config',
    TREE_DATA: 'treeData',
    VISUALIZATION: 'visualization',
    PHYLOCANVAS: 'phylocanvas',
    UI_STATE: 'uiState',
    CUSTOM_COLORS: 'customColors',
    LEAF_CUSTOM_COLORS: 'leafCustomColors',
    RENDERER_TYPE: 'rendererType',
  },

  _saveInProgress: false,
  _pendingSave: false,
  _readOnly: false,
  _permissionReason: null,
  onPermissionChange: null,

  async initialize() {
    if (!tableau?.extensions?.settings) {
      throw new Error('Tableau Extensions API not available');
    }

    // Viewing mode is known up front, so the user can be told before spending
    // effort on a tree that will not be stored. Saves are still attempted:
    // this is an advisory signal, only a rejected saveAsync stops them.
    const permission = resolveSavePermission({ mode: tableau?.extensions?.environment?.mode });
    if (!permission.canPersist && permission.reason) {
      this._reportPermission(permission.reason);
    }

    console.log('[TableauSettings] Settings manager initialized');
  },

  /** True when settings changes are not expected to survive a reload. */
  isReadOnly() {
    return this._permissionReason !== null;
  },

  _reportPermission(reason) {
    if (this._permissionReason === reason) return;
    // A rejected save is definite and outranks the advisory viewing-mode signal.
    if (this._permissionReason === 'permission-denied') return;
    this._permissionReason = reason;

    const message = describeSavePermission(reason);
    console.warn(`[TableauSettings] ${message.title}: ${message.detail}`);

    if (!this.onPermissionChange) return;
    try {
      this.onPermissionChange({ canPersist: false, reason }, message);
    } catch (error) {
      console.error('[TableauSettings] Permission handler failed:', error);
    }
  },

  _isPermissionDenied(error) {
    return isPermissionDeniedError(error);
  },

  async _executeSave() {
    if (this._readOnly) return;

    if (this._saveInProgress) {
      this._pendingSave = true;
      return;
    }

    this._saveInProgress = true;

    try {
      await tableau.extensions.settings.saveAsync();
      console.log('[Settings] Settings saved to Tableau');
    } catch (error) {
      if (this._isPermissionDenied(error)) {
        this._readOnly = true;
        this._pendingSave = false;
        this._reportPermission('permission-denied');
        return;
      }
      const msg = error instanceof Error ? error.message : String(error);
      console.error('[Settings] STUCK at Step 2: Tableau save failed -', msg);
      throw error;
    } finally {
      this._saveInProgress = false;

      if (this._pendingSave) {
        this._pendingSave = false;
        // Intentionally not awaited, coalesced trailing save runs independently.
        void this._executeSave();
      }
    }
  },

  async saveConfig(config) {
    try {
      tableau.extensions.settings.set(this.KEYS.CONFIG, JSON.stringify(config));
      await this._executeSave();
    } catch (error) {
      console.error('[TableauSettings] Failed to save config:', error);
      throw error;
    }
  },

  loadConfig() {
    try {
      const configStr = tableau.extensions.settings.get(this.KEYS.CONFIG);
      if (!configStr) return null;
      return JSON.parse(configStr) as KronosConfig;
    } catch (error) {
      console.error('[TableauSettings] Failed to load config:', error);
      return null;
    }
  },

  async saveTreeData(treeData) {
    try {
      const data: TreeData = {
        content: treeData.content,
        fileName: treeData.fileName ?? 'tree.nwk',
        timestamp: Date.now(),
      };
      tableau.extensions.settings.set(this.KEYS.TREE_DATA, JSON.stringify(data));
      await this._executeSave();
    } catch (error) {
      console.error('[TableauSettings] Failed to save tree data:', error);
      throw error;
    }
  },

  loadTreeData() {
    try {
      const treeDataStr = tableau.extensions.settings.get(this.KEYS.TREE_DATA);
      if (!treeDataStr) return null;
      return JSON.parse(treeDataStr) as TreeData;
    } catch (error) {
      console.error('[TableauSettings] Failed to load tree data:', error);
      return null;
    }
  },

  async saveVisualizationSettings(settings) {
    try {
      const vizSettings: VisualizationSettings = {
        colorScheme: settings.colorScheme ?? 'default',
        labelType: settings.labelType ?? 'taxonName',
        showBranchLengths: settings.showBranchLengths ?? true,
        branchLengthScale: settings.branchLengthScale ?? 'linear',
        nodeSize: settings.nodeSize ?? 5,
        layout: settings.layout ?? 'rectangular',
        timestamp: Date.now(),
      };
      tableau.extensions.settings.set(this.KEYS.VISUALIZATION, JSON.stringify(vizSettings));
      await this._executeSave();
    } catch (error) {
      console.error('[TableauSettings] Failed to save visualization settings:', error);
      throw error;
    }
  },

  loadVisualizationSettings() {
    try {
      const vizStr = tableau.extensions.settings.get(this.KEYS.VISUALIZATION);
      if (!vizStr) return null;
      return JSON.parse(vizStr) as VisualizationSettings;
    } catch (error) {
      console.error('[TableauSettings] Failed to load visualization settings:', error);
      return null;
    }
  },

  async savePhylocanvasSettings(settings) {
    try {
      tableau.extensions.settings.set(this.KEYS.PHYLOCANVAS, JSON.stringify(settings));
      await this._executeSave();
    } catch (error) {
      console.error('[TableauSettings] Failed to save phylocanvas settings:', error);
      throw error;
    }
  },

  loadPhylocanvasSettings() {
    try {
      const phyloStr = tableau.extensions.settings.get(this.KEYS.PHYLOCANVAS);
      if (!phyloStr) return null;
      return JSON.parse(phyloStr) as Record<string, unknown>;
    } catch (error) {
      console.error('[TableauSettings] Failed to load phylocanvas settings:', error);
      return null;
    }
  },

  async saveRendererType(rendererType) {
    try {
      tableau.extensions.settings.set(this.KEYS.RENDERER_TYPE, rendererType);
      await this._executeSave();
    } catch (error) {
      console.error('[TableauSettings] Failed to save renderer type:', error);
      throw error;
    }
  },

  loadRendererType() {
    try {
      const value = tableau.extensions.settings.get(this.KEYS.RENDERER_TYPE);
      return value ?? null;
    } catch (error) {
      console.error('[TableauSettings] Failed to load renderer type:', error);
      return null;
    }
  },

  async saveUIState(uiState) {
    try {
      tableau.extensions.settings.set(this.KEYS.UI_STATE, JSON.stringify(uiState));
      await this._executeSave();
    } catch (error) {
      console.error('[TableauSettings] Failed to save UI state:', error);
      throw error;
    }
  },

  loadUIState() {
    try {
      const uiStateStr = tableau.extensions.settings.get(this.KEYS.UI_STATE);
      if (!uiStateStr) return null;
      return JSON.parse(uiStateStr) as UIState;
    } catch (error) {
      console.error('[TableauSettings] Failed to load UI state:', error);
      return null;
    }
  },

  async saveCustomColors(customColors) {
    try {
      tableau.extensions.settings.set(this.KEYS.CUSTOM_COLORS, JSON.stringify(customColors));
      await this._executeSave();
    } catch (error) {
      console.error('[TableauSettings] Failed to save custom colors:', error);
      throw error;
    }
  },

  loadCustomColors() {
    try {
      const colorsStr = tableau.extensions.settings.get(this.KEYS.CUSTOM_COLORS);
      if (!colorsStr) return null;
      return JSON.parse(colorsStr) as Record<string, unknown>;
    } catch (error) {
      console.error('[TableauSettings] Failed to load custom colors:', error);
      return null;
    }
  },

  async clearCustomColors() {
    try {
      tableau.extensions.settings.erase(this.KEYS.CUSTOM_COLORS);
      await this._executeSave();
      console.log('[TableauSettings] Cleared all custom colors');
    } catch (error) {
      console.error('[TableauSettings] Failed to clear custom colors:', error);
    }
  },

  async saveLeafCustomColors(leafCustomColors) {
    try {
      tableau.extensions.settings.set(this.KEYS.LEAF_CUSTOM_COLORS, JSON.stringify(leafCustomColors));
      await this._executeSave();
    } catch (error) {
      console.error('[TableauSettings] Failed to save leaf custom colors:', error);
      throw error;
    }
  },

  loadLeafCustomColors() {
    try {
      const colorsStr = tableau.extensions.settings.get(this.KEYS.LEAF_CUSTOM_COLORS);
      if (!colorsStr) return null;
      return JSON.parse(colorsStr) as Record<string, unknown>;
    } catch (error) {
      console.error('[TableauSettings] Failed to load leaf custom colors:', error);
      return null;
    }
  },

  async saveAllSettings(allSettings) {
    try {
      if (allSettings.config) {
        tableau.extensions.settings.set(this.KEYS.CONFIG, JSON.stringify(allSettings.config));
      }
      if (allSettings.treeData) {
        tableau.extensions.settings.set(this.KEYS.TREE_DATA, JSON.stringify(allSettings.treeData));
      }
      if (allSettings.visualization) {
        tableau.extensions.settings.set(this.KEYS.VISUALIZATION, JSON.stringify(allSettings.visualization));
      }
      if (allSettings.phylocanvas) {
        tableau.extensions.settings.set(this.KEYS.PHYLOCANVAS, JSON.stringify(allSettings.phylocanvas));
      }
      if (allSettings.uiState) {
        tableau.extensions.settings.set(this.KEYS.UI_STATE, JSON.stringify(allSettings.uiState));
      }
      if (allSettings.customColors) {
        tableau.extensions.settings.set(this.KEYS.CUSTOM_COLORS, JSON.stringify(allSettings.customColors));
      }
      if (allSettings.leafCustomColors) {
        tableau.extensions.settings.set(this.KEYS.LEAF_CUSTOM_COLORS, JSON.stringify(allSettings.leafCustomColors));
      }

      // Single saveAsync call to avoid race conditions.
      await this._executeSave();
    } catch (error) {
      console.error('[TableauSettings] Failed to save all settings:', error);
      throw error;
    }
  },

  loadAllSettings() {
    try {
      return {
        config: this.loadConfig(),
        treeData: this.loadTreeData(),
        visualization: this.loadVisualizationSettings(),
        phylocanvas: this.loadPhylocanvasSettings(),
        uiState: this.loadUIState(),
        customColors: this.loadCustomColors(),
        leafCustomColors: this.loadLeafCustomColors(),
      };
    } catch (error) {
      console.error('[TableauSettings] Failed to load all settings:', error);
      return {};
    }
  },

  async clearSetting(key) {
    try {
      tableau.extensions.settings.erase(key);
      await this._executeSave();
      console.log('[TableauSettings] Setting cleared:', key);
    } catch (error) {
      console.error('[TableauSettings] Failed to clear setting:', error);
      throw error;
    }
  },

  async clearAllSettings() {
    try {
      const allKeys = Object.values(this.KEYS);
      allKeys.forEach((key) => {
        tableau.extensions.settings.erase(key);
      });
      await this._executeSave();
      console.log('[TableauSettings] All settings cleared');
    } catch (error) {
      console.error('[TableauSettings] Failed to clear all settings:', error);
      throw error;
    }
  },

  getAllRawSettings() {
    try {
      const allSettings = tableau.extensions.settings.getAll();
      console.log('[TableauSettings] Raw settings:', allSettings);
      return allSettings;
    } catch (error) {
      console.error('[TableauSettings] Failed to get raw settings:', error);
      return {};
    }
  },
};
